import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@/icons';
import { Modal } from './Modal';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';

export interface EditTakeoverModalProps {
    isOpen: boolean;
    /** Display name of whoever took over editing (e.g. "First Last"), or undefined if unknown. */
    editorName?: string;
    /** True when the taker is the current user in another tab/window (vs. a different user). */
    isSameUser?: boolean;
    onTakeBackControl: () => void;
    onReturnHome: () => void;
}

/**
 * ConcurrentCaseEditGuard live presence: shown when a background poll (useCaseEditingLock) finds
 * that ANOTHER user's editing marker has replaced yours on the case you're editing -- i.e.
 * someone else opened/took over this case. Non-dismissible (no close button / backdrop close):
 * the user must choose. "Take back control" re-marks the case as theirs (so the other session
 * then sees this same modal); "Return to homepage" leaves the wizard. See AUTOSAVE_DETAILS.md §17.
 */
export const EditTakeoverModal: React.FC<EditTakeoverModalProps> = ({ isOpen, editorName, isSameUser, onTakeBackControl, onReturnHome }) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);

    if (!isOpen) {
        return null;
    }

    const who = editorName && editorName.trim() !== '' ? editorName : 'Someone else';
    const title = isSameUser ? 'This case is now open in another tab' : 'Someone else is now editing this case';
    const headline = isSameUser
        ? 'You have this case open in another tab or window'
        : `${who} has taken over editing this case`;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onReturnHome}
            title={title}
            headerBackgroundColor={theme.colors.status.warning}
            showCloseButton={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing.md,
                    padding: theme.spacing.md,
                    backgroundColor: '#fef3c7',
                    borderRadius: theme.borderRadius.sm,
                    border: `1px solid ${theme.colors.status.warning || '#f59e0b'}`,
                }}>
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        style={{ fontSize: theme.typography.fontSize.lg, color: '#92400e', marginTop: theme.spacing.xs }}
                    />
                    <div style={{ flex: 1 }}>
                        <p style={{
                            ...themedStyles.text.body,
                            color: '#92400e',
                            fontWeight: theme.typography.fontWeight.medium,
                            margin: `0 0 ${theme.spacing.xs} 0`,
                        }}>
                            {headline}
                        </p>
                        <p style={{
                            ...themedStyles.text.body,
                            color: '#92400e',
                            fontSize: theme.typography.fontSize.sm,
                            margin: 0,
                        }}>
                            {isSameUser
                                ? 'Only one tab should edit at a time, or your changes may overwrite each other. Take back control here to keep editing in this tab, or return to the homepage.'
                                : 'If you keep working, your changes may overwrite theirs. Take back control to resume editing here (they’ll be notified), or return to the homepage.'}
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                    <button
                        type="button"
                        onClick={onTakeBackControl}
                        style={themedStyles.button.primary}
                    >
                        Take back control
                    </button>
                    <button
                        type="button"
                        onClick={onReturnHome}
                        style={themedStyles.button.secondary}
                    >
                        Return to homepage
                    </button>
                </div>
            </div>
        </Modal>
    );
};
